import axios from 'axios';

function readStringMessage(value: unknown): string | undefined {
  if (typeof value === 'string' && value.trim().length > 0) {
    return value.trim();
  }

  return undefined;
}

const OBJECT_OBJECT_MESSAGE = '[object Object]';

function isUsableErrorMessage(message: string | undefined): message is string {
  return Boolean(message && message !== OBJECT_OBJECT_MESSAGE);
}

/** Reads user-facing text from API JSON bodies including `{ error: { message } }`. */
export function parseApiErrorMessageFromJson(parsed: unknown): string | undefined {
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return undefined;
  }

  const data = parsed as Record<string, unknown>;
  const nestedError =
    data.error && typeof data.error === 'object' && !Array.isArray(data.error)
      ? (data.error as Record<string, unknown>)
      : undefined;

  const message =
    readStringMessage(data.message) ??
    (nestedError ? readStringMessage(nestedError.message) : undefined) ??
    readStringMessage(data.error) ??
    readStringMessage(data.detail) ??
    (nestedError ? readStringMessage(nestedError.code) : undefined);

  return isUsableErrorMessage(message) ? message : undefined;
}

export function parseApiErrorMessageFromResponseText(
  response: Response,
  text: string,
  fallback: string
): string {
  if (text.trim()) {
    try {
      const parsed = JSON.parse(text) as unknown;
      const fromJson = parseApiErrorMessageFromJson(parsed);
      if (fromJson) {
        return fromJson;
      }
    } catch {
      const trimmed = text.trim();
      if (isUsableErrorMessage(trimmed)) {
        return trimmed;
      }
    }
  }

  const statusText = response.statusText.trim();
  return isUsableErrorMessage(statusText) ? statusText : fallback;
}

function parseApiErrorMessageFromAxiosData(data: unknown): string | undefined {
  return parseApiErrorMessageFromJson(data);
}

/** Reads message from axios/fetch errors including `{ error: { message } }`. */
export function getApiErrorMessageFromUnknown(error: unknown, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const responseMessage = parseApiErrorMessageFromAxiosData(error.response?.data);
    if (responseMessage) {
      return responseMessage;
    }
  }

  if (error instanceof Error) {
    const trimmed = error.message.trim();
    if (isUsableErrorMessage(trimmed)) {
      return trimmed;
    }
  }

  return fallback;
}
